// Ported from the standalone Lead Quality Score project's api.js. Originally
// a raw-fetch client with its own base-URL/env handling (import.meta.env is
// Vite-only, not available under this CRA app); rewritten to go through the
// CRM's own shared axios instance (src/api/axios.js) instead, so this tool
// automatically gets the same Bearer-token attachment, refresh-token retry,
// and error shape as every other feature in the app. Function names and
// return shapes are unchanged, so the ported pages/components need no
// changes beyond their import path.
import api from '../api/axios';

// Mounted at /api/lead-quality on the backend (not /api/brands etc.) to
// avoid colliding with this app's own Shoot Plan /api/brands.
const BASE = '/lead-quality';

function unwrapError(err) {
  const detail = err?.response?.data?.detail || err?.message || 'Something went wrong.';
  const wrapped = new Error(detail);
  wrapped.detail = detail;
  throw wrapped;
}

export const api2 = {
  listBrands: () => api.get(`${BASE}/brands`).then((r) => r.data).catch(unwrapError),
  getBrand: (id) => api.get(`${BASE}/brands/${id}`).then((r) => r.data).catch(unwrapError),
  createBrand: (data) => api.post(`${BASE}/brands`, data).then((r) => r.data).catch(unwrapError),
  deleteBrand: (id) => api.delete(`${BASE}/brands/${id}`).then((r) => r.data).catch(unwrapError),

  listLeads: (brandId) => api.get(`${BASE}/leads`, { params: { brand: brandId } }).then((r) => r.data).catch(unwrapError),
  getLead: (id) => api.get(`${BASE}/leads/${id}`).then((r) => r.data).catch(unwrapError),
  createLead: (data) => api.post(`${BASE}/leads`, data).then((r) => r.data).catch(unwrapError),
  updateLead: (id, data) => api.patch(`${BASE}/leads/${id}`, data).then((r) => r.data).catch(unwrapError),
  deleteLead: (id) => api.delete(`${BASE}/leads/${id}`).then((r) => r.data).catch(unwrapError),

  listMonthlyUploads: (brandId) =>
    api.get(`${BASE}/monthly-uploads`, { params: { brand: brandId } }).then((r) => r.data).catch(unwrapError),
  getMonthlyUpload: (id) => api.get(`${BASE}/monthly-uploads/${id}`).then((r) => r.data).catch(unwrapError),
  createMonthlyUpload: (brandId, month, file) => {
    const formData = new FormData();
    formData.append('brand', brandId);
    formData.append('month', month);
    formData.append('file', file);
    return api
      .post(`${BASE}/monthly-uploads`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data)
      .catch((err) => {
        if (!err.response) {
          // The request never got an answer: server not running / connection
          // lost, or the browser could not read the chosen file (e.g. it is
          // open or locked in Excel).
          const wrapped = new Error('network');
          wrapped.detail =
            'Could not complete the upload. Check that the backend is running, and if the file is open in another program (such as Excel), close it and choose the file again.';
          throw wrapped;
        }
        return unwrapError(err);
      });
  },
  deleteMonthlyUpload: (id) => api.delete(`${BASE}/monthly-uploads/${id}`).then((r) => r.data).catch(unwrapError),
};

// Keep the original import name (`import { api } from '../api'`) working
// across every ported page/component without touching their imports.
export { api2 as api };
